import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import DeleteIcon from '@src/svgs/DeleteIcon';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import SpeakersIcon from '@src/svgs/SpeakersIcon';
import UndoIcon from '@src/svgs/UndoIcon';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

interface TranscriptSpeakerContextMenuProps {
  isOpen: boolean;
  targetElement: HTMLElement | null;
  onClose: () => void;
  onReassignEverywhere: () => void;
  onReassignSectionOnly?: () => void;
  onUnassign: () => void;
  onEdit: () => void;
  onUndoSectionReassign?: () => void;
  /** True if the speaker is anonymous (no voice mapping) */
  isAnonymous?: boolean;
  /** True if this section has been overridden via section reassignment */
  hasSectionOverride?: boolean;
}

/**
 * Context menu shown when clicking a speaker label in the transcript.
 *
 * Assigned speakers: Edit, Unassign, Reassign Speaker, Reassign Selection Only
 * Unassigned speakers: Assign Speaker, Assign Speaker for Selection Only
 */
const TranscriptSpeakerContextMenu: React.FC<
  TranscriptSpeakerContextMenuProps
> = ({
  isOpen,
  targetElement,
  onClose,
  onReassignEverywhere,
  onReassignSectionOnly,
  onUnassign,
  onEdit,
  onUndoSectionReassign,
  isAnonymous = false,
  hasSectionOverride = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    if (!isOpen) return;

    const openedAt = Date.now();
    const DEBOUNCE_MS = 150;

    const handleOutsideClick = (e: MouseEvent) => {
      if (Date.now() - openedAt < DEBOUNCE_MS) return;

      const isOutsideMenu =
        menuRef.current && !menuRef.current.contains(e.target as Node);
      const isOutsideTarget = !targetElement?.contains(e.target as Node);

      if (isOutsideMenu && isOutsideTarget) {
        onClose();
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, targetElement]);

  // Position menu relative to the clicked speaker label
  useEffect(() => {
    if (!isOpen || !menuRef.current || !targetElement) return;

    const updatePosition = () => {
      if (!menuRef.current || !targetElement) return;

      const targetRect = targetElement.getBoundingClientRect();
      const menuElement = menuRef.current;
      const menuHeight = menuElement.offsetHeight;

      // Position below the speaker label, left-aligned
      let left = targetRect.left;
      let top = targetRect.bottom + 4;

      // Prevent going off the right edge
      const menuWidth = menuElement.offsetWidth;
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10;
      }

      // Prevent going off the bottom edge - show above instead
      if (top + menuHeight > window.innerHeight) {
        top = targetRect.top - menuHeight - 4;
      }

      menuElement.style.position = 'fixed';
      menuElement.style.top = `${top}px`;
      menuElement.style.left = `${left}px`;
    };

    updatePosition();

    let rafId: number | null = null;
    const scheduleUpdate = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updatePosition);
    };

    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isOpen, targetElement]);

  if (!isOpen) return null;

  // Build menu items, then interleave with dividers
  const items: React.ReactNode[] = [];

  if (isAnonymous) {
    items.push(
      <MenuItem
        key="assign"
        onClick={() => {
          onReassignEverywhere();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <MicrophoneIcon width="14" height="14" fillColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Assign Speaker</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
    if (onReassignSectionOnly) {
      items.push(
        <MenuItem
          key="assign-section"
          onClick={() => {
            onReassignSectionOnly();
            onClose();
          }}
        >
          <MenuContent>
            <MenuIconAndText>
              <MenuIconWrapper>
                <MicrophoneIcon
                  width="14"
                  height="14"
                  fillColor="currentColor"
                />
              </MenuIconWrapper>
              <MenuLabel>Assign Speaker for Selection Only</MenuLabel>
            </MenuIconAndText>
          </MenuContent>
        </MenuItem>,
      );
    }
  } else {
    items.push(
      <MenuItem
        key="edit"
        onClick={() => {
          onEdit();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <SpeakersIcon width="14" height="14" strokeColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Edit Speaker</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
    items.push(
      <MenuItem
        key="unassign"
        onClick={() => {
          onUnassign();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <DeleteIcon width="13" height="13" strokeColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Unassign Speaker</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
    items.push(
      <MenuItem
        key="reassign"
        onClick={() => {
          onReassignEverywhere();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <MicrophoneIcon width="14" height="14" fillColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Reassign Speaker</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
    if (onReassignSectionOnly) {
      items.push(
        <MenuItem
          key="reassign-section"
          onClick={() => {
            onReassignSectionOnly();
            onClose();
          }}
        >
          <MenuContent>
            <MenuIconAndText>
              <MenuIconWrapper>
                <MicrophoneIcon
                  width="14"
                  height="14"
                  fillColor="currentColor"
                />
              </MenuIconWrapper>
              <MenuLabel>Reassign Selection Only</MenuLabel>
            </MenuIconAndText>
          </MenuContent>
        </MenuItem>,
      );
    }
  }

  if (hasSectionOverride && onUndoSectionReassign) {
    items.push(
      <MenuItem
        key="undo-reassign"
        onClick={() => {
          onUndoSectionReassign();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <UndoIcon width="14" height="14" strokeColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Undo Selection Reassign</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  const menuContent = (
    <MenuContainer ref={menuRef}>
      <MenuItems>{items}</MenuItems>
    </MenuContainer>
  );

  return ReactDOM.createPortal(menuContent, document.body);
};

const MenuContainer = styled.div`
  position: fixed;
  background: var(--background);
  box-shadow: var(--elevation-toast);
  border-radius: var(--radius);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  z-index: var(${TOP_LAYER_VARS.contextMenu}, 1000006);
`;

const MenuItems = styled.div`
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--background);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: var(--spacing-xs);
  overflow: hidden;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  padding: 1px 6px;
  width: 100%;
  cursor: pointer;
  border: none;
  outline: none;
  background: none;
  border-radius: var(--radius-button);
  color: var(--foreground);

  &:hover {
    background: var(--surface-elevated);
  }
`;

const MenuContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  padding: 9px 10px;
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
`;

const MenuIconAndText = styled.div`
  display: flex;
  align-items: center;
  text-align: start;
  gap: 8px;
  width: 100%;
`;

const MenuIconWrapper = styled.div`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuLabel = styled.p`
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  color: var(--foreground);
  margin: 0;
  white-space: nowrap;
`;

export default TranscriptSpeakerContextMenu;
